import React, { useState, useRef, useEffect } from 'react';
import './Portfolio.css';
import CarelyProject from '../../assets/CarelyProject.png';
import WordLensProject from '../../assets/WordLensProject.png';
import AIdaptProject from '../../assets/Group 5.png';
import LeAIProject from '../../assets/Group 12.png';
import MoneyHungryProject from '../../assets/Group 10.png';
import ReuseProject from '../../assets/Group 11.png';
import { FaApple, FaAppStore, FaGithub } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';
import { LuBookOpen, LuWallet } from 'react-icons/lu';
import { FiExternalLink, FiArrowUpRight } from 'react-icons/fi';

const data = [
  {
    id: 1,
    title: 'Carely',
    subtitle: 'iOS · App Store',
    category: 'iOS App',
    description:
      'Built for iOS 26+, Carely connects volunteers to Long-Term Care Facilities for the elderly. Uses Foundation Models on-device for smart matching, Liquid Glass UI and a fully native CloudKit and SwiftData stack.',
    tags: ['Swift 6', 'SwiftUI', 'Foundation Models', 'SwiftData', 'CloudKit', 'Local LLM'],
    cta: { label: 'GitHub', href: 'https://github.com/VitorFSLacerda/TheGentlemen', icon: <FaGithub /> },
    image: CarelyProject,
  },
  {
    id: 2,
    title: 'WordLens',
    subtitle: 'iOS · Swift Student Challenge 2026',
    category: 'AI · Computer Vision',
    description:
      'Mixes crossword puzzles with augmented reality and on-device computer vision. The user looks around to find the real-world object matching each clue, a multimodal experience powered by Vision and Foundation Models.',
    tags: ['SwiftUI', 'Vision', 'CoreML', 'AR', 'GenAI', 'On-Device AI', 'Python'],
    cta: { label: 'GitHub', href: 'https://github.com/felipalb/wwdc26', icon: <FaGithub /> },
    image: WordLensProject,
  },
  {
    id: 3,
    title: 'LeAI',
    subtitle: 'iOS · Idiap Coding Challenge 2026',
    category: 'AI · Accessibility',
    description:
      'iOS accessibility prototype for blind and low-vision readers, built for the Idiap Coding Challenge (ICC) 2026. Captures printed book pages, extracts text, continues from the last reading position and runs a voice-first reading flow. Includes an optional opt-in backend for AI summaries.',
    tags: ['SwiftUI', 'Vision OCR', 'AVFoundation', 'Node.js', 'SDD', 'Harness Engineering', 'Claude Code API'],
    cta: { label: 'GitHub', href: 'https://github.com/felipalb/ICC-LeAI-Project', icon: <FaGithub /> },
    image: LeAIProject,
  },
  {
    id: 4,
    title: 'MoneyHungry',
    subtitle: 'AI Agents · Personal Finance',
    category: 'AI · Multi-Agent',
    description:
      'Multi-agent system that turns raw financial data into personalized money decisions. Specialized agents reason over spending, goals and market context, then collaborate to produce actionable plans. Built end-to-end with modern LLMs and an agentic orchestration layer.',
    tags: ['Agents', 'LLM Orchestration', 'Python', 'GenAI', 'Fintech', 'Gemini API'],
    cta: { label: 'GitHub', href: 'https://github.com/felipalb/foodhungry', icon: <FaGithub /> },
    image: MoneyHungryProject,
  },
  {
    id: 5,
    title: 'AIdapt',
    subtitle: 'iOS · TestFlight',
    category: 'AI · Multi-Agent',
    description:
      'iOS app that customizes your resume for every job. A multi-agent pipeline on Vertex AI (Gemini 2.5 Flash & Pro) parses the CV, researches the company, scores requirements, rewrites bullets and audits the output, streaming progress to the app via SSE.',
    tags: ['SwiftUI', 'Gemini', 'Vertex AI', 'FastAPI', 'Firestore', 'SSE', 'A2A', 'Harness Engineering', 'SDD'],
    cta: { label: 'GitHub', href: 'https://github.com/felipalb/TheHigers', icon: <FaGithub /> },
    image: AIdaptProject,
  },
  {
    id: 6,
    title: 'Reuse',
    subtitle: 'iOS · Sustainability',
    category: 'iOS App',
    description:
      'iOS app focused on corporate sustainability through gamification. Turns everyday reuse actions into measurable impact for teams. Built end-to-end during the Apple Developer Academy.',
    tags: ['SwiftUI', 'Gamification', 'Firebase', 'MVVM', 'Sustainability', 'SDD'],
    cta: { label: 'GitHub', href: 'https://github.com/amaral-spec/The-Forgers/tree/develop', icon: <FaGithub /> },
    image: ReuseProject,
  },
];

const Portfolio = () => {
  const [flipped, setFlipped] = useState({});
  const cardRefs = useRef([]);
  const [uniformHeight, setUniformHeight] = useState(null);

  useEffect(() => {
    document.fonts.ready.then(() => {
      const heights = cardRefs.current.map((ref) => ref?.offsetHeight || 0);
      const maxHeight = Math.max(...heights);
      if (maxHeight > 0) setUniformHeight(maxHeight);
    });
  }, []);

  const toggleFlip = (id) => {
    setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="portfolio">
      <h5>Selected work</h5>
      <h2>Projects</h2>

      <div className='container portfolio__container'>
        {data.map(({ id, title, subtitle, category, description, tags, cta, image, art }, index) => (
          <div
            ref={(el) => (cardRefs.current[index] = el)}
            className={`portfolio__card${flipped[id] ? ' flipped' : ''}`}
            key={id}
            onClick={() => toggleFlip(id)}
            style={uniformHeight ? { height: `${uniformHeight}px` } : {}}
          >
            <div className='portfolio__card-inner'>
              {/* Front */}
              <article className='portfolio__item portfolio__face portfolio__front'>
                <div className='portfolio__front-head'>
                  <span className='portfolio__category'>{category}</span>
                  <FiArrowUpRight className='portfolio__flip-hint' />
                </div>

                <div className='portfolio__title-block'>
                  <h3>{title}</h3>
                  <span className='portfolio__subtitle'>{subtitle}</span>
                </div>

                <p className='portfolio__description'>{description}</p>

                <div className='portfolio__tags'>
                  {tags.map((t) => (
                    <span key={t} className='portfolio__tag'>{t}</span>
                  ))}
                </div>

                <div className='portfolio__item-cta'>
                  <a
                    href={cta.href}
                    className='btn portfolio__btn'
                    target='_blank'
                    rel='noreferrer'
                    onClick={(e) => e.stopPropagation()}
                  >
                    {cta.icon} {cta.label} <FiExternalLink />
                  </a>
                </div>
              </article>

              {/* Back */}
              <article
                className='portfolio__item portfolio__face portfolio__back'
                style={art ? { background: art.gradient } : undefined}
              >
                {image ? (
                  <img src={image} alt={title} className='portfolio__back-image' />
                ) : (
                  <div className='portfolio__art'>
                    <span className='portfolio__art-icon'>{art?.icon}</span>
                    <span className='portfolio__art-title'>{title}</span>
                  </div>
                )}
              </article>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Portfolio;
